import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Church, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const generateAbbreviation = (name: string) =>
  name
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .map((word) => word[0].toUpperCase())
    .join("");

export default function ChurchSetupPage() {
  const { user, refreshRole } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const [churchName, setChurchName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("United States");
  const [zipCode, setZipCode] = useState("");
  const [churchPhone, setChurchPhone] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setIsLoading(true);
    try {
      const { data: createdChurchId, error } = await supabase.rpc("register_church" as any, {
        _user_id: user.id,
        _church_name: churchName.trim(),
        _address: address.trim(),
        _city: city.trim(),
        _state: state.trim(),
        _country: country.trim() || "United States",
        _zip_code: zipCode.trim(),
        _phone: churchPhone.trim() || null,
        _email: user.email,
      });
      if (error) throw error;
      if (!createdChurchId) throw new Error("Church was created but no church ID was returned.");

      // Ensure the new church is linked to the current user role before continuing.
      let linked = false;
      for (let i = 0; i < 8; i++) {
        const { data: roleRow, error: roleError } = await supabase
          .from("user_roles")
          .select("church_id")
          .eq("user_id", user.id)
          .maybeSingle();
        if (roleError) throw roleError;
        if (roleRow?.church_id === createdChurchId) {
          linked = true;
          break;
        }
        await delay(150);
      }
      if (!linked) {
        throw new Error("Church created, but your account linkage is still pending. Please try again.");
      }

      const abbreviation = generateAbbreviation(churchName.trim()) || null;
      if (abbreviation) {
        const { error: abbreviationError } = await supabase
          .from("church_profile")
          .update({ abbreviation })
          .eq("id", createdChurchId);
        if (abbreviationError) throw abbreviationError;
      }

      // Seed built-in default ministry for first-time churches.
      const { data: generalMinistry, error: generalCheckError } = await supabase
        .from("organizations")
        .select("id")
        .eq("church_id", createdChurchId)
        .eq("type", "ministry")
        .eq("level", "organization")
        .ilike("name", "general")
        .limit(1)
        .maybeSingle();
      if (generalCheckError) throw generalCheckError;

      if (!generalMinistry) {
        const { error: createGeneralError } = await supabase.from("organizations").insert({
          name: "General",
          description: "Built-in default ministry",
          type: "ministry",
          level: "organization",
          parent_id: null,
          church_id: createdChurchId,
        });
        if (createGeneralError) throw createGeneralError;
      }

      const { data: createdProfile, error: profileError } = await supabase
        .from("church_profile")
        .select("*")
        .eq("id", createdChurchId)
        .maybeSingle();
      if (profileError) throw profileError;
      if (!createdProfile) throw new Error("Church profile was not found after creation.");

      // Prime cache for this user/session so billing can resolve church context immediately.
      queryClient.setQueryData(["church_profile", user.id], createdProfile);

      await queryClient.invalidateQueries({ queryKey: ["church_profile", user.id] });
      await queryClient.invalidateQueries({ queryKey: ["user_role_church"] });
      await queryClient.invalidateQueries({ queryKey: ["subscription"] });
      await refreshRole();

      toast.success("Church profile created!");
      navigate("/dashboard");
    } catch (err: any) {
      toast.error(err.message || "Failed to create church profile");
    }
    setIsLoading(false);
  };

  return (
    <div className="relative min-h-screen overflow-hidden brand-page-bg flex items-center justify-center p-4">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 left-0 h-72 w-72 rounded-full bg-[#dbe9ff] blur-3xl" />
        <div className="absolute right-0 top-20 h-72 w-72 rounded-full bg-[#f8efe2] blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#1e3a8a] flex items-center justify-center mx-auto mb-4 shadow-md shadow-[#1e3a8a]/20">
            <Church className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Set Up Your Church</h1>
          <p className="text-slate-600 mt-1 text-sm">Tell us about your ministry to get started</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-border bg-card p-6 shadow-[0_16px_40px_-24px_rgba(2,6,23,0.35)] space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="churchName">Church Name <span className="text-destructive">*</span></Label>
            <Input
              id="churchName"
              placeholder="Grace Community Church"
              value={churchName}
              onChange={(e) => setChurchName(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Street Address <span className="text-destructive">*</span></Label>
            <Input
              id="address"
              placeholder="123 Main Street"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="city">City <span className="text-destructive">*</span></Label>
              <Input
                id="city"
                placeholder="Charleston"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State / Province <span className="text-destructive">*</span></Label>
              <Input
                id="state"
                placeholder="SC"
                value={state}
                onChange={(e) => setState(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                placeholder="United States"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="zipCode">ZIP / Postal Code <span className="text-destructive">*</span></Label>
              <Input
                id="zipCode"
                placeholder="10001"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="churchPhone">Church Phone</Label>
            <Input
              id="churchPhone"
              placeholder="+1 (555) 000-0000"
              value={churchPhone}
              onChange={(e) => setChurchPhone(e.target.value)}
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-[#1e3a8a] hover:bg-[#162d70]"
            disabled={isLoading}
          >
            {isLoading ? "Creating church profile..." : "Continue to Dashboard →"}
          </Button>
        </form>
      </div>
    </div>
  );
}
